/**
 * Segmented Dwell Handling
 *
 * Finds optimal spot chains when no single spot can accommodate an aircraft's full dwell.
 * This enables the temporal parking algorithm to place aircraft across multiple spots with
 * planned moves between them during the dwell period.
 *
 * KEY BEHAVIORS:
 *
 * 1. GREEDY WINDOW SELECTION
 *    Always picks the spot with the longest available window from the current bucket position.
 *    This naturally prefers single-spot placements when possible, only creating multi-spot
 *    splits when genuinely required by capacity constraints.
 *
 * 2. SKIP-AHEAD LOGIC
 *    If no spot is available at the aircraft's arrival time (all spots occupied), the algorithm
 *    can "skip ahead" to the first available window, creating a delayed/partial placement.
 *    This is indicated by the first segment starting after the aircraft's actual arrival.
 *
 * 3. REAL-WORLD USE CASES
 *    Multi-spot splits are most valuable in production scenarios with:
 *    - Immovable aircraft already on ramp (pre-existing occupancy constraints)
 *    - Spot restrictions/priorities (certain aircraft limited to certain zones)
 *    - Peak capacity periods (arrival clustering during busy times)
 *    - Long planning horizons (multi-day scheduling with complex overlaps)
 *
 * 4. TESTING CHALLENGES
 *    In "clean" test scenarios with empty initial states, the greedy algorithm typically finds
 *    single-spot solutions because it has maximum flexibility. This doesn't mean the feature is
 *    broken - it means the algorithm is working efficiently. True multi-spot splits naturally
 *    emerge from real-world constraints, not artificial test cases.
 */

import { findAvailableWindows, orInto } from "./bitmap";
import { ParkingRequest, PlacementSegment, Spot } from "./types";

/**
 * Spot chain: sequence of spots covering an aircraft's dwell
 * @property segments - Ordered list of time/spot segments
 * @property moveCount - Number of planned moves (segments.length - 1)
 */
type SpotChain = {
  segments: PlacementSegment[];
  moveCount: number;
};

/**
 * Find optimal spot chain to cover a parking request's full dwell
 *
 * ALGORITHM:
 * - Greedy: Always pick the spot that covers the most time from the current position
 * - Skip-ahead: If arrival blocked, jump to first available window (partial placement)
 * - Move cap: Respects plannedMoveCap limit on number of moves allowed
 *
 * @param request - Parking request needing placement
 * @param spots - Available spots to consider
 * @param plannedMoveCap - Maximum number of moves allowed (segments - 1)
 * @param bufferMinutes - Buffer to add before/after dwell
 * @param horizonHours - Planning horizon in hours (default: 24)
 * @returns Best chain found, or null if impossible within move cap
 */
export function findSpotChain(
  request: ParkingRequest,
  spots: Spot[],
  plannedMoveCap: number,
  bufferMinutes: number = 0,
  horizonHours: number = 24
): SpotChain | null {
  const bufferBuckets = Math.ceil(bufferMinutes / 15);
  const dwellStart = Math.max(0, request.arrivalBucket - bufferBuckets);
  const totalSlots = horizonHours * 4;
  const dwellEnd = Math.min(
    totalSlots,
    request.departureBucket + bufferBuckets
  );

  console.log(
    `[findSpotChain] Request ${request.tail_number} needs coverage ${dwellStart}-${dwellEnd}, plannedMoveCap=${plannedMoveCap}`
  );

  // Greedy chain building: iteratively pick the spot with longest coverage from current position
  const segments: PlacementSegment[] = [];
  let currentBucket = dwellStart;

  while (currentBucket < dwellEnd) {
    // STEP 1: Find the spot with the longest available window covering currentBucket
    let bestSpot: Spot | null = null;
    let bestWindow: [number, number] | null = null;
    let maxCoverage = 0;

    for (const spot of spots) {
      // Find all available windows in this spot's occupancy bitmap
      // We search the full range [dwellStart, dwellEnd) to see all gaps
      const windows = findAvailableWindows(
        spot.occupancy,
        dwellStart,
        dwellEnd
      );

      console.log(
        `[findSpotChain]   Spot ${spot.id} windows from ${dwellStart}:`,
        JSON.stringify(windows)
      );

      // Check each window to see if it covers our current position
      for (const [start, end] of windows) {
        // Window must overlap currentBucket (start <= currentBucket < end)
        // This includes windows that:
        // - Start before currentBucket (continuing from previous segment)
        // - Start exactly at currentBucket (ideal handoff)
        if (start <= currentBucket && end > currentBucket) {
          // Greedy: pick the window with maximum coverage from currentBucket forward
          const coverage = end - currentBucket;
          if (coverage > maxCoverage) {
            maxCoverage = coverage;
            bestSpot = spot;
            bestWindow = [start, end];
          }
        }
      }
    }

    // STEP 2: Handle case where no spot available at currentBucket
    if (!bestSpot || !bestWindow) {
      // SKIP-AHEAD LOGIC: Only allowed for the first segment (delayed arrival)
      // This creates a partial placement where the aircraft parks later than requested
      if (segments.length === 0) {
        console.log(
          `[findSpotChain]   No spot at bucket ${currentBucket}, searching for earliest available window...`
        );

        // Find the earliest window that starts AFTER currentBucket
        let earliestStart = Infinity;
        for (const spot of spots) {
          const windows = findAvailableWindows(
            spot.occupancy,
            dwellStart,
            dwellEnd
          );
          for (const [start, end] of windows) {
            if (start > currentBucket && start < earliestStart) {
              earliestStart = start;
              bestSpot = spot;
              bestWindow = [start, end];
              maxCoverage = end - start;
            }
          }
        }

        if (bestSpot && bestWindow) {
          console.log(
            `[findSpotChain]   Skipping ahead to bucket ${earliestStart}, using ${bestSpot.id}`
          );
          currentBucket = earliestStart; // Jump forward to first availability
        } else {
          console.log(
            `[findSpotChain]   No future windows available - chain impossible`
          );
          return null;
        }
      } else {
        // Mid-chain gap: cannot skip ahead after first segment (would create disconnect)
        console.log(
          `[findSpotChain]   No spot available at bucket ${currentBucket} - chain impossible`
        );
        return null;
      }
    }

    console.log(
      `[findSpotChain]   Best: ${bestSpot.id} covers ${currentBucket}-${bestWindow[1]}`
    );

    // STEP 3: Add segment covering from currentBucket to end of selected window
    segments.push({
      spotId: bestSpot.id,
      orientationId: bestSpot.orientations[0].id,
      startBucket: currentBucket,
      endBucket: bestWindow[1],
    });

    // STEP 4: Advance to next uncovered bucket
    currentBucket = bestWindow[1];

    // STEP 5: Check if we exceeded the planned move cap
    const moveCount = segments.length - 1;
    if (moveCount > plannedMoveCap) {
      console.log(
        `[findSpotChain]   Exceeded move cap: ${moveCount} > ${plannedMoveCap}`
      );
      return null; // Too many moves required
    }
  }

  // ============================================================================
  // PARTIAL PLACEMENT PREVENTION
  // ============================================================================
  // Validate that the chain covers the ENTIRE trip duration with no gaps.
  // This prevents partial placements where aircraft are only placed for part
  // of their trip (e.g., placed May 22-23 but trip runs May 22-28).
  //
  // Real-world rationale: FBOs don't do partial assignments. Aircraft are either
  // fully accommodated or the operator finds alternative arrangements.
  //
  // TO RE-ENABLE PARTIAL PLACEMENTS (if an FBO requests this feature):
  // 1. Comment out or remove this entire validation block (lines below)
  // 2. OR add a config parameter: allowPartialPlacements: boolean
  // 3. Update ParkingConfig type and UI to expose the toggle
  // 4. Gate this validation: if (!allowPartialPlacements) { ... }
  //
  // NOTE: Partial placements can occur in three ways:
  // - Gap at start: first segment starts AFTER trip arrival
  // - Gap at end: last segment ends BEFORE trip departure
  // - Gap in middle: segments are not continuous (multi-segment chains)
  // ============================================================================

  const firstSegmentStart = segments[0].startBucket;
  const lastSegmentEnd = segments[segments.length - 1].endBucket;

  // Check 1: Verify chain starts at trip arrival (no gap at beginning)
  if (firstSegmentStart > dwellStart) {
    console.log(
      `[findSpotChain]   Rejecting partial placement: chain starts at bucket ${firstSegmentStart}, ` +
      `but trip arrives at bucket ${dwellStart} (gap: ${firstSegmentStart - dwellStart} buckets = ` +
      `${(firstSegmentStart - dwellStart) * 0.25} hours)`
    );
    return null;
  }

  // Check 2: Verify chain ends at trip departure (no gap at end)
  if (lastSegmentEnd < dwellEnd) {
    console.log(
      `[findSpotChain]   Rejecting partial placement: chain ends at bucket ${lastSegmentEnd}, ` +
      `but trip departs at bucket ${dwellEnd} (gap: ${dwellEnd - lastSegmentEnd} buckets = ` +
      `${(dwellEnd - lastSegmentEnd) * 0.25} hours)`
    );
    return null;
  }

  // Check 3: Verify no gaps between segments (for multi-segment chains)
  for (let i = 0; i < segments.length - 1; i++) {
    const currentEnd = segments[i].endBucket;
    const nextStart = segments[i + 1].startBucket;

    if (currentEnd !== nextStart) {
      console.log(
        `[findSpotChain]   Rejecting partial placement: gap between segments ${i} and ${i + 1}. ` +
        `Segment ${i} (${segments[i].spotId}) ends at bucket ${currentEnd}, ` +
        `segment ${i + 1} (${segments[i + 1].spotId}) starts at bucket ${nextStart} ` +
        `(gap: ${nextStart - currentEnd} buckets = ${(nextStart - currentEnd) * 0.25} hours)`
      );
      return null;
    }
  }

  console.log(
    `[findSpotChain]   Success! Chain covers full trip ${dwellStart}-${dwellEnd}:`,
    segments.map((s) => `${s.spotId}(${s.startBucket}-${s.endBucket})`)
  );

  return {
    segments,
    moveCount: segments.length - 1,
  };
}

/**
 * Apply a spot chain to the placement and reserve bitmaps
 *
 * Iterates through each segment in the chain and marks the corresponding time buckets
 * as occupied in the spot's occupancy bitmap. This prevents future aircraft from being
 * placed in the same spot during those time periods.
 *
 * IMPORTANT: This function mutates the spot occupancy bitmaps in-place.
 *
 * @param chain - Spot chain to apply (from findSpotChain)
 * @param spots - Spot array (will be mutated)
 */
export function applySpotChain(chain: SpotChain, spots: Spot[]): void {
  for (const segment of chain.segments) {
    const spot = spots.find((s) => s.id === segment.spotId);
    if (!spot) {
      throw new Error(`Spot ${segment.spotId} not found`);
    }

    // Create a bitmap mask for this segment's time range
    // Each bit represents a 15-minute bucket (96 buckets = 24 hours)
    const mask = new BigUint64Array(2);
    for (
      let bucket = segment.startBucket;
      bucket < segment.endBucket;
      bucket++
    ) {
      const wordIndex = Math.floor(bucket / 64);
      const bitIndex = bucket % 64;
      mask[wordIndex] |= 1n << BigInt(bitIndex);
    }

    // OR the mask into the spot's occupancy bitmap to reserve these buckets
    orInto(spot.occupancy, mask);
  }
}
